import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ArchiveRecord } from '../types/moderation';
import {
  claimSubmission,
  getSubmission,
  releaseSubmission,
} from '../lib/api';
import { ApiError } from '../lib/api';
import { useCurrentUser } from '../lib/auth';
import StatusBadge from './StatusBadge';
import ErrorState from './ErrorState';
import Skeleton from './ui/Skeleton';
import MediaCarousel from './MediaCarousel';
import MobileActionComposer, {
  type MobileComposerAction,
} from './MobileActionComposer';
import { formatDateTime, relativeTime } from '../lib/format';
import { neighborIds } from '../lib/submission-nav';
import { canQuickApprove } from './ModerationActions';

interface MobileSubmissionDetailProps {
  submissionId: string;
  onBack: () => void;
}

type ComposerState =
  | { open: false }
  | { open: true; action: MobileComposerAction };

/**
 * Mobile-first replacement for SubmissionDetail.
 *
 * Layout:
 *   [back / refresh]
 *   [full-bleed media carousel]
 *   [title + chips]
 *   [AI verdict card]
 *   [accordion: metadata]
 *   [accordion: contributor]
 *   [accordion: history]
 *   [accordion: internal notes]
 *   [sticky bottom action bar: Reject / Approve / More]
 *
 * The AI verdict, sensitivity banner, and action verbs are above the
 * fold so triage stays a one-thumb interaction. Metadata and contributor
 * are collapsed by default; the moderator expands only what they need.
 *
 * The composer (reasons + notes) is a bottom sheet over the screen so
 * the keyboard and safe-area inset are handled in one place.
 */
export default function MobileSubmissionDetail({
  submissionId,
  onBack,
}: MobileSubmissionDetailProps) {
  const [record, setRecord] = useState<ArchiveRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [composer, setComposer] = useState<ComposerState>({ open: false });
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    metadata: false,
    contributor: false,
    history: false,
    notes: false,
  });
  const currentUser = useCurrentUser();

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setNotFound(false);
    getSubmission(submissionId)
      .then((data) => {
        if (cancelled) return;
        setRecord(data);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setNotFound(true);
          setLoading(false);
          return;
        }
        setError((err as Error).message || 'Failed to load submission');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [submissionId]);

  useEffect(() => {
    const cleanup = load();
    return cleanup;
  }, [load]);

  // Best-effort claim on open / release on close. Mirrors desktop
  // behaviour. Failures are swallowed because the lock UI surfaces
  // them once the record loads.
  useEffect(() => {
    let cancelled = false;
    claimSubmission(submissionId)
      .then((res) => {
        if (cancelled) return;
        setRecord((prev) => (prev ? { ...prev, claim: res.claim } : prev));
      })
      .catch(() => {
        // Ignore; the claim banner takes care of surfacing the conflict.
      });
    return () => {
      cancelled = true;
      releaseSubmission(submissionId).catch(() => undefined);
    };
  }, [submissionId]);

  const refresh = useCallback(() => {
    setRefreshing(true);
    getSubmission(submissionId)
      .then((data) => setRecord(data))
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) {
          setNotFound(true);
          return;
        }
        setError((err as Error).message || 'Failed to refresh');
      })
      .finally(() => setRefreshing(false));
  }, [submissionId]);

  const { prev, next } = useMemo(
    () => neighborIds(submissionId),
    [submissionId, record],
  );

  const navigateById = (id: string) => {
    window.location.hash = `#/submissions/${encodeURIComponent(id)}`;
  };

  const toggleSection = (key: string) =>
    setOpenSections((s) => ({ ...s, [key]: !s[key] }));

  if (loading) {
    return (
      <div
        className="mobile-detail"
        aria-busy="true"
        data-testid="mobile-detail-loading"
      >
        <header className="mobile-detail__topbar">
          <button
            type="button"
            className="mobile-detail__back"
            onClick={onBack}
            aria-label="Back to queue"
            data-testid="mobile-detail-back"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
              <path
                d="M12 4l-5 6 5 6"
                stroke="currentColor"
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>Queue</span>
          </button>
        </header>
        <div className="mobile-detail__media-skeleton">
          <Skeleton variant="block" />
        </div>
        <div className="mobile-detail__body">
          <Skeleton variant="title" width="80%" />
          <Skeleton variant="text" />
          <Skeleton variant="text" width="60%" />
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="mobile-detail">
        <header className="mobile-detail__topbar">
          <button
            type="button"
            className="mobile-detail__back"
            onClick={onBack}
            data-testid="mobile-detail-back"
          >
            <span aria-hidden="true">←</span>
            <span>Queue</span>
          </button>
        </header>
        <div
          className="error-state"
          role="alert"
          data-testid="mobile-detail-not-found"
        >
          Submission not found.
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mobile-detail">
        <header className="mobile-detail__topbar">
          <button
            type="button"
            className="mobile-detail__back"
            onClick={onBack}
            data-testid="mobile-detail-back"
          >
            <span aria-hidden="true">←</span>
            <span>Queue</span>
          </button>
        </header>
        <ErrorState
          message="We could not load this submission."
          detail={error}
          onRetry={() => load()}
          testId="mobile-detail-error"
        />
      </div>
    );
  }

  if (!record) return null;

  const role = currentUser?.role ?? 'volunteer';
  const claim = record.claim;
  const claimedByOther =
    claim && currentUser && claim.userId !== currentUser.sub ? claim : null;
  const isLeadOrAdmin = role === 'lead_moderator' || role === 'admin';

  const status = record.moderationStatus;
  const canApprove =
    status === 'pending_review' ||
    status === 'rejected' ||
    status === 'awaiting_contributor' ||
    status === 'awaiting_peer_review';
  const canReject =
    status === 'pending_review' ||
    status === 'approved' ||
    status === 'awaiting_contributor' ||
    status === 'awaiting_peer_review';
  const canWithdraw = status === 'approved';
  const canRequestChanges = status === 'pending_review';
  const quickApproveEligible = canQuickApprove(record);

  const aiTrace =
    record.aiAnalysis && record.aiAnalysis.length > 0
      ? record.aiAnalysis[record.aiAnalysis.length - 1]
      : null;
  const aiPct = aiTrace ? Math.round(aiTrace.confidence * 100) : null;
  const aiBand = aiTrace
    ? aiTrace.confidence >= 0.85
      ? 'high'
      : aiTrace.confidence >= 0.6
      ? 'med'
      : 'low'
    : null;
  const aiSafetyFlag = aiTrace
    ? aiTrace.signals.safety.graphicSexual.detected ||
      aiTrace.signals.safety.graphicViolence.detected ||
      aiTrace.signals.safety.spam.detected
    : false;
  const aiPii = aiTrace?.signals.pii.detected ?? false;
  const aiDuplicate = aiTrace?.signals.duplicate.isDuplicate ?? false;
  // Surface the AI's safety / PII signals as a "review before approving"
  // banner above the action bar so the moderator sees the warning before
  // they tap Approve. (The AI does not have a dedicated sensitive flag,
  // we infer it from a fired safety/PII signal on an unflagged record.)
  const aiSensitiveSuggested =
    !record.sensitive && (aiSafetyFlag || aiPii);

  const openComposer = (action: MobileComposerAction) =>
    setComposer({ open: true, action });

  return (
    <div className="mobile-detail" data-testid="mobile-detail">
      <header className="mobile-detail__topbar">
        <button
          type="button"
          className="mobile-detail__back"
          onClick={onBack}
          aria-label="Back to queue"
          data-testid="mobile-detail-back"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
            <path
              d="M12 4l-5 6 5 6"
              stroke="currentColor"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>Queue</span>
        </button>
        <div className="mobile-detail__topbar-spacer" />
        <button
          type="button"
          className="mobile-detail__topbar-btn"
          onClick={refresh}
          disabled={refreshing}
          aria-label="Refresh"
          data-testid="mobile-detail-refresh"
        >
          {refreshing ? '…' : '↻'}
        </button>
        {prev && (
          <button
            type="button"
            className="mobile-detail__topbar-btn"
            onClick={() => prev && navigateById(prev)}
            aria-label="Previous submission"
            data-testid="mobile-detail-prev"
          >
            ←
          </button>
        )}
        {next && (
          <button
            type="button"
            className="mobile-detail__topbar-btn"
            onClick={() => next && navigateById(next)}
            aria-label="Next submission"
            data-testid="mobile-detail-next"
          >
            →
          </button>
        )}
      </header>

      {claimedByOther && (
        <div
          className="claim-banner mobile-detail__banner"
          role="status"
          data-testid="mobile-claim-banner"
        >
          <span>
            Being reviewed by <strong>{claimedByOther.userEmail}</strong>.
            Actions are disabled until they release the claim.
          </span>
        </div>
      )}

      {record.moderationStatus === 'awaiting_peer_review' &&
        record.peerReview && (
          <div
            className="peer-review-banner mobile-detail__banner"
            role="status"
            data-testid="mobile-peer-banner"
          >
            Awaiting peer review. First vote (
            <strong>{record.peerReview.firstReview.action}</strong>) by{' '}
            <strong>{record.peerReview.firstReview.userEmail}</strong>.
          </div>
        )}

      <div className="mobile-detail__media" data-testid="mobile-detail-media">
        <MediaCarousel
          submissionId={record.submissionId}
          files={record.files || []}
          externalVideoUrl={record.externalVideoUrl}
          transcript={record.transcript}
        />
      </div>

      <div className="mobile-detail__body">
        <h1 className="mobile-detail__title">
          {record.title || 'Untitled submission'}
        </h1>
        <div className="mobile-detail__chips">
          <StatusBadge status={record.moderationStatus} />
          {record.mediaType && (
            <span className="chip chip--media-type">{record.mediaType}</span>
          )}
          {record.decades && record.decades[0] && (
            <span className="chip">{record.decades[0]}</span>
          )}
          {record.sensitive && (
            <span
              className="chip chip--sensitive"
              data-testid="mobile-detail-sensitive-chip"
            >
              Sensitive
            </span>
          )}
        </div>

        {aiSensitiveSuggested && (
          <div
            className="mobile-detail__sensitive-banner"
            role="status"
            data-testid="mobile-sensitive-banner"
          >
            AI suggests this may be sensitive. Review before approving.
          </div>
        )}

        {aiTrace && (
          <section
            className={`mobile-detail__ai mobile-detail__ai--${aiTrace.decision}`}
            data-testid="mobile-ai-card"
          >
            <header className="mobile-detail__ai-header">
              <span className="mobile-detail__ai-verdict">
                {aiTrace.decision === 'auto_approved'
                  ? 'AI: auto-approve'
                  : aiTrace.decision === 'auto_rejected'
                  ? 'AI: auto-reject'
                  : 'AI: human review'}
              </span>
              <span
                className={`mobile-detail__ai-conf mobile-detail__ai-conf--${aiBand}`}
              >
                {aiPct}% ({aiBand})
              </span>
            </header>
            <div className="mobile-detail__ai-chips">
              <span
                className={`mobile-detail__ai-chip mobile-detail__ai-chip--${
                  aiSafetyFlag ? 'warn' : 'good'
                }`}
              >
                {aiSafetyFlag ? 'Safety flag' : 'Safe'}
              </span>
              <span
                className={`mobile-detail__ai-chip mobile-detail__ai-chip--${
                  aiPii ? 'warn' : 'good'
                }`}
              >
                {aiPii ? 'PII' : 'PII clean'}
              </span>
              <span
                className={`mobile-detail__ai-chip mobile-detail__ai-chip--${
                  aiDuplicate ? 'warn' : 'good'
                }`}
              >
                {aiDuplicate ? 'Duplicate' : 'Unique'}
              </span>
            </div>
            {aiTrace.signals.metadata.summary && (
              <p className="mobile-detail__ai-summary">
                {aiTrace.signals.metadata.summary}
              </p>
            )}
            {(aiTrace.signals.metadata.themes?.length ||
              aiTrace.signals.metadata.decades?.length) && (
              <div className="mobile-detail__ai-suggest">
                <span className="mobile-detail__ai-suggest-label">
                  Suggested:
                </span>
                {(aiTrace.signals.metadata.themes || [])
                  .slice(0, 3)
                  .map((t) => (
                    <span key={t} className="mobile-detail__ai-suggest-chip">
                      {t}
                    </span>
                  ))}
                {(aiTrace.signals.metadata.decades || [])
                  .slice(0, 2)
                  .map((d) => (
                    <span
                      key={d}
                      className="mobile-detail__ai-suggest-chip mobile-detail__ai-suggest-chip--decade"
                    >
                      {d}
                    </span>
                  ))}
              </div>
            )}
          </section>
        )}

        <Accordion
          id="metadata"
          title="Metadata"
          open={openSections.metadata}
          onToggle={() => toggleSection('metadata')}
        >
          <dl className="mobile-detail__metadata">
            {record.description && (
              <>
                <dt>Description</dt>
                <dd>{record.description}</dd>
              </>
            )}
            {(record.yearStart || record.yearEnd) && (
              <>
                <dt>Years</dt>
                <dd>
                  {record.yearStart}
                  {record.yearEnd && record.yearEnd !== record.yearStart
                    ? ` to ${record.yearEnd}`
                    : ''}
                </dd>
              </>
            )}
            {record.themes && record.themes.length > 0 && (
              <>
                <dt>Themes</dt>
                <dd>{record.themes.join(', ')}</dd>
              </>
            )}
            {record.decades && record.decades.length > 0 && (
              <>
                <dt>Decades</dt>
                <dd>{record.decades.join(', ')}</dd>
              </>
            )}
            {record.location?.text && (
              <>
                <dt>Location</dt>
                <dd>{record.location.text}</dd>
              </>
            )}
            {record.sourceUrl && (
              <>
                <dt>Source</dt>
                <dd>
                  <a href={record.sourceUrl} target="_blank" rel="noreferrer">
                    {record.sourceUrl}
                  </a>
                </dd>
              </>
            )}
            <dt>Submitted</dt>
            <dd>
              {relativeTime(record.createdAt) ||
                formatDateTime(record.createdAt)}
            </dd>
            <dt>Last updated</dt>
            <dd>{formatDateTime(record.updatedAt)}</dd>
          </dl>
          <p className="mobile-detail__editing-hint">
            Tap a field on the desktop view to edit. Mobile editing arrives
            in a follow-up.
          </p>
        </Accordion>

        <Accordion
          id="contributor"
          title="Contributor"
          open={openSections.contributor}
          onToggle={() => toggleSection('contributor')}
        >
          <dl className="mobile-detail__metadata">
            <dt>Name</dt>
            <dd>{record.contributor?.name || 'Anonymous'}</dd>
            {record.contributor?.email && (
              <>
                <dt>Email</dt>
                <dd>
                  <a href={`mailto:${record.contributor.email}`}>
                    {record.contributor.email}
                  </a>
                </dd>
              </>
            )}
            {record.contributor?.attributionPref && (
              <>
                <dt>Attribution</dt>
                <dd>{record.contributor.attributionPref}</dd>
              </>
            )}
            {record.copyrightHolder && (
              <>
                <dt>Copyright holder</dt>
                <dd>{record.copyrightHolder}</dd>
              </>
            )}
            {record.collectionCredit && (
              <>
                <dt>Collection credit</dt>
                <dd>{record.collectionCredit}</dd>
              </>
            )}
            <dt>Consent</dt>
            <dd>
              {record.consent?.timestamp
                ? formatDateTime(record.consent.timestamp)
                : 'Recorded'}{' '}
              (v{record.consent?.version || '1.0'})
            </dd>
          </dl>
        </Accordion>

        <Accordion
          id="history"
          title={`History (${record.moderationHistory?.length || 0})`}
          open={openSections.history}
          onToggle={() => toggleSection('history')}
        >
          {!record.moderationHistory ||
          record.moderationHistory.length === 0 ? (
            <p className="mobile-detail__muted">No history yet.</p>
          ) : (
            <ul className="mobile-detail__history">
              {record.moderationHistory.map((entry, i) => (
                <li key={i}>
                  <strong>{entry.action}</strong>{' '}
                  <span className="mobile-detail__muted">
                    by {entry.userEmail}
                  </span>
                  <br />
                  <span className="mobile-detail__muted">
                    {formatDateTime(entry.timestamp)}
                  </span>
                  {entry.note && <p>{entry.note}</p>}
                </li>
              ))}
            </ul>
          )}
        </Accordion>

        {record.internalNotes && (
          <Accordion
            id="notes"
            title="Internal notes"
            open={openSections.notes}
            onToggle={() => toggleSection('notes')}
          >
            <p className="mobile-detail__notes">{record.internalNotes}</p>
          </Accordion>
        )}
      </div>

      <nav
        className="mobile-detail__action-bar"
        aria-label="Moderation actions"
        data-testid="mobile-action-bar"
      >
        {canReject && (
          <button
            type="button"
            className="mobile-detail__action mobile-detail__action--reject"
            onClick={() => openComposer('rejected')}
            disabled={Boolean(claimedByOther)}
            data-testid="mobile-action-reject"
            aria-keyshortcuts="r"
          >
            <span aria-hidden="true">✕</span> Reject
          </button>
        )}
        {canApprove && (
          <button
            type="button"
            className={`mobile-detail__action mobile-detail__action--approve${
              quickApproveEligible
                ? ' mobile-detail__action--approve-quick'
                : ''
            }`}
            onClick={() => openComposer('approved')}
            disabled={Boolean(claimedByOther)}
            data-testid="mobile-action-approve"
            aria-keyshortcuts="a"
          >
            <span aria-hidden="true">✓</span>{' '}
            {quickApproveEligible ? 'Approve' : 'Approve'}
          </button>
        )}
        {(canRequestChanges || canWithdraw || isLeadOrAdmin) && (
          <MoreActionsMenu
            canRequestChanges={canRequestChanges}
            canWithdraw={canWithdraw}
            disabled={Boolean(claimedByOther)}
            onPick={openComposer}
          />
        )}
      </nav>

      <MobileActionComposer
        open={composer.open}
        action={composer.open ? composer.action : null}
        record={record}
        onOpenChange={(o) => {
          if (!o) setComposer({ open: false });
        }}
        onModerated={() => {
          refresh();
        }}
        disabled={Boolean(claimedByOther)}
      />
    </div>
  );
}

interface AccordionProps {
  id: string;
  title: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

function Accordion({ id, title, open, onToggle, children }: AccordionProps) {
  return (
    <section
      className={`mobile-detail__section${
        open ? ' mobile-detail__section--open' : ''
      }`}
      data-testid={`mobile-section-${id}`}
    >
      <button
        type="button"
        className="mobile-detail__section-trigger"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={`mobile-section-body-${id}`}
        data-testid={`mobile-section-trigger-${id}`}
      >
        <span>{title}</span>
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          aria-hidden="true"
          className={`mobile-detail__section-chevron${
            open ? ' mobile-detail__section-chevron--open' : ''
          }`}
        >
          <path
            d="M3 5l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.8"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && (
        <div
          className="mobile-detail__section-body"
          id={`mobile-section-body-${id}`}
        >
          {children}
        </div>
      )}
    </section>
  );
}

interface MoreActionsMenuProps {
  canRequestChanges: boolean;
  canWithdraw: boolean;
  disabled: boolean;
  onPick: (action: MobileComposerAction) => void;
}

function MoreActionsMenu({
  canRequestChanges,
  canWithdraw,
  disabled,
  onPick,
}: MoreActionsMenuProps) {
  const [open, setOpen] = useState(false);

  // Click outside / Escape close.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!canRequestChanges && !canWithdraw) return null;

  return (
    <div className="mobile-detail__more">
      <button
        type="button"
        className="mobile-detail__action mobile-detail__action--more"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        data-testid="mobile-action-more"
        aria-label="More actions"
      >
        ⋯
      </button>
      {open && (
        <>
          <button
            type="button"
            className="mobile-detail__more-scrim"
            aria-label="Close menu"
            onClick={() => setOpen(false)}
          />
          <ul className="mobile-detail__more-menu" role="menu">
            {canRequestChanges && (
              <li>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    onPick('awaiting_contributor');
                    setOpen(false);
                  }}
                  data-testid="mobile-action-request-changes"
                >
                  Request changes
                </button>
              </li>
            )}
            {canWithdraw && (
              <li>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    onPick('withdrawn');
                    setOpen(false);
                  }}
                  data-testid="mobile-action-withdraw"
                >
                  Withdraw
                </button>
              </li>
            )}
          </ul>
        </>
      )}
    </div>
  );
}
